"use client";

import { ArrowUpIcon } from "lucide-react";
import { memo } from "react";

import { Spinner } from "@/components/loader/spinner";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupTextarea,
} from "@/components/ui/input-group";
import { cn } from "@/lib/utils";

type ChatComposerProps = {
  value: string;
  isSending: boolean;
  onValueChange: (value: string) => void;
  onSend: (text: string) => Promise<void> | void;
  placeholder?: string;
  className?: string;
};

export const ChatComposer = memo(function ChatComposer({
  value,
  isSending,
  onValueChange,
  onSend,
  placeholder = "Message AI Planner...",
  className,
}: ChatComposerProps) {
  const canSend = value.trim().length > 0 && !isSending;

  return (
    <form
      className="w-full min-w-0"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSend) return;
        void onSend(value);
      }}
    >
      <InputGroup
        className={cn(
          "h-auto rounded-2xl border-border/75 bg-background/95 shadow-[0_16px_40px_-28px_rgba(15,23,42,0.8)] transition-all duration-300",
          className,
        )}
      >
        <InputGroupTextarea
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (canSend) {
                void onSend(value);
              }
            }
          }}
          placeholder={placeholder}
          className="min-h-24 min-w-0 max-h-52 px-4 py-4 text-[15px] leading-relaxed"
        />

        <InputGroupAddon
          align="block-end"
          className="flex w-full min-w-0 flex-wrap items-center justify-between gap-3 border-t border-border/70 px-2.5 pt-2"
        >
          <InputGroupText className="min-w-0 text-xs text-muted-foreground/90 sm:truncate">
            Enter to send, Shift+Enter for new line
          </InputGroupText>

          <InputGroupButton
            type="submit"
            variant="default"
            size="sm"
            disabled={!canSend}
            className="rounded-xl px-3.5 transition-transform duration-200 enabled:hover:scale-105 enabled:active:scale-95"
            aria-label="Send message"
          >
            <span className="relative inline-flex size-4">
              <span
                className={cn(
                  "absolute inset-0 inline-flex items-center justify-center transition-all duration-200",
                  isSending ? "scale-100 opacity-100" : "scale-0 opacity-0",
                )}
              >
                <Spinner className="size-4" />
              </span>
              <span
                className={cn(
                  "absolute inset-0 inline-flex items-center justify-center transition-all duration-200",
                  isSending ? "scale-0 opacity-0" : "scale-100 opacity-100",
                )}
              >
                <ArrowUpIcon className="size-4" />
              </span>
            </span>
            <span>Send</span>
            <span className="sr-only">Send</span>
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </form>
  );
});
